/**
 * Group Members Tab Component
 * 모임 멤버 목록 표시 및 관리
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Users, Shield, UserCheck, UserX, MessageCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { useLocation } from "wouter";

interface Member {
  id: number;
  user_uid: string;
  role: string;
  status: string;
  joined_at: string;
  email?: string;
  name?: string;
  avatar_url?: string;
}

interface GroupMembersTabProps {
  groupId: number | null;
  isOwner: boolean;
}

export function GroupMembersTab({ groupId, isOwner }: GroupMembersTabProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();

  // 멤버 목록 조회
  const { data: membersData, isLoading } = useQuery<{ members: Member[] }>({
    queryKey: ["/api/community/groups", groupId, "members"],
    queryFn: async () => {
      if (!groupId) throw new Error("Invalid group ID");
      const response = await fetch(`/api/community/groups/${groupId}/members`);
      if (!response.ok) throw new Error("Failed to fetch members");
      return response.json();
    },
    enabled: !!groupId,
  });

  const members = membersData?.members || [];
  const activeMembers = members.filter((m) => m.status === "active");
  const pendingMembers = members.filter((m) => m.status === "pending");

  // 멤버 승인
  const approveMemberMutation = useMutation({
    mutationFn: async (memberUid: string) => {
      if (!user || !groupId) throw new Error("로그인이 필요합니다");
      const token = await user.getIdToken();
      const response = await fetch(`/api/community/groups/${groupId}/members/${memberUid}/approve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "승인에 실패했습니다");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/community/groups", groupId, "members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/community/groups", groupId] });
      toast({
        title: "멤버가 승인되었습니다",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "오류",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // 멤버 거부
  const rejectMemberMutation = useMutation({
    mutationFn: async (memberUid: string) => {
      if (!user || !groupId) throw new Error("로그인이 필요합니다");
      const token = await user.getIdToken();
      const response = await fetch(`/api/community/groups/${groupId}/members/${memberUid}/reject`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "거부에 실패했습니다");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/community/groups", groupId, "members"] });
      toast({
        title: "가입 요청이 거부되었습니다",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "오류",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <Card className="p-12 text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/2 mx-auto" />
          <div className="h-4 bg-muted rounded w-1/3 mx-auto" />
        </div>
      </Card>
    );
  }

  const getRoleBadge = (role: string) => {
    switch (role) {
      case "owner":
        return <Badge variant="default">주최자</Badge>;
      case "admin":
        return <Badge variant="secondary">운영진</Badge>;
      default:
        return <Badge variant="outline">멤버</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Active Members */}
      <div>
        <h3 className="text-lg font-semibold mb-4">멤버 ({activeMembers.length}명)</h3>
        {activeMembers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeMembers.map((member) => (
              <Card key={member.id} className="hover-elevate">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {member.avatar_url ? (
                      <img
                        src={member.avatar_url}
                        alt={member.name || member.email}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                        <Users className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm truncate">
                          {member.name || member.email || "익명"}
                        </span>
                        {getRoleBadge(member.role)}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(member.joined_at), {
                          addSuffix: true,
                          locale: ko,
                        })}
                      </p>
                      {user && member.user_uid !== user.uid && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="mt-2"
                          onClick={() => navigate(`/chat-start/user/${member.user_uid}`)}
                        >
                          <MessageCircle className="w-3 h-3 mr-1" />
                          메시지
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">멤버가 없습니다</p>
          </Card>
        )}
      </div>

      {/* Pending Members (Owner/Admin only) */}
      {isOwner && pendingMembers.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">승인 대기 ({pendingMembers.length}명)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pendingMembers.map((member) => (
              <Card key={member.id} className="hover-elevate">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {member.avatar_url ? (
                      <img
                        src={member.avatar_url}
                        alt={member.name || member.email}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                        <Users className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm truncate">
                          {member.name || member.email || "익명"}
                        </span>
                        <Badge variant="outline">대기 중</Badge>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="default"
                          className="flex-1"
                          onClick={() => approveMemberMutation.mutate(member.user_uid)}
                          disabled={approveMemberMutation.isPending}
                        >
                          <UserCheck className="w-3 h-3 mr-1" />
                          {approveMemberMutation.isPending ? "처리 중..." : "승인"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => rejectMemberMutation.mutate(member.user_uid)}
                          disabled={rejectMemberMutation.isPending}
                        >
                          <UserX className="w-3 h-3 mr-1" />
                          {rejectMemberMutation.isPending ? "처리 중..." : "거부"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
